import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import Proposal from "work/proposals/proposal";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";

const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
  width: "100%",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  // No left margin: the table meets the divider between the two columns. `HeadControls` above it
  // already carries the prose inset — see `workPaneProseInset`.
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type ConflictsTabProps = {
  proposal: Proposal;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const { proposal } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const conflictsDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download document. Please try again later.", { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (!proposal) return null;

  if (Masked.isMasked(proposal.conflictsCheckWaived) || Masked.isMasked(proposal.conflicts))
    return maskedProposalFieldNotice(MaskedProposalField.Conflicts);

  return (
    <>
      <HeadControls>
        <FormControlLabel
          control={<Switch disabled={true} checked={proposal?.conflictsCheckWaived.value ?? false} color="primary" />}
          label={
            proposal?.conflictsCheckWaived.value ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
      </HeadControls>
      <ListContainer waived={proposal?.conflictsCheckWaived.value ?? false} container direction="row">
        <Grid>
          {proposal.conflicts.length === 0 && (
            <HelperText>No conflicts documents</HelperText>
          )}
          {proposal.conflicts.length > 0 && (
            <DocumentList
              documents={proposal?.conflicts ?? []}
              documentType={WorkDocumentType.Conflicts}
              downloadingFileId={isDownloadingFile}
              onDownload={handleDownloadDocumentById}
            />
          )}
        </Grid>
      </ListContainer>
    </>
  );
}
